import { useEffect, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { endOfDay, startOfDay } from 'date-fns'

import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { Button } from '@gto/ui/button'
import type { ColumnDef, PaginationState } from '@gto/ui/data-table'
import { DateRangePicker } from '@gto/ui/date-picker'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminOrderRowSchema } from '@/api/gen'
import { useGetPartner, useListOrders } from '@/api/gen'
import { CommissionsTable } from '@/components/commissions-table'
import { EmptyState } from '@/components/empty-state'
import { FacetChip } from '@/components/facet-chip'
import { SearchInput, SelectField } from '@/components/form'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { fluidColumn } from '@/components/table-columns'
import { useDateShortcuts } from '@/hooks/use-date-shortcuts'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import type { CommissionStatus } from '@/utils/commission-status'
import { COMMISSION_STATUSES } from '@/utils/commission-status'
import { fromDateStr, toDateStr } from '@/utils/date-str'
import type { OrdersSearch } from '@/utils/orders-search-params'
import { toListOrdersParams } from '@/utils/orders-search-params'

const DEFAULT_PAGE_SIZE = 25

const STATUS_LABEL_IDS: Record<CommissionStatus, string> = {
  LOCKED: 'admin.orders.status.locked',
  UNLOCKED: 'admin.orders.status.unlocked',
  PAID: 'admin.orders.status.paid',
  REFUNDED: 'admin.orders.status.refunded',
}

// `<TextField select>` uses the empty string for "no selection"; the URL
// schema uses `undefined`. Same sentinel boundary as the partners filter bar.
const ALL = ''

// Route API handle — the route exports its `validateSearch` schema, so
// `Route.useSearch()` here is fully typed against `OrdersSearch`.
const ordersRoute = getRouteApi('/_private/admin/orders')

const usePartnerColumn = (): ColumnDef<AffiliateAdminOrderRowSchema> => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()

  return {
    accessorKey: 'partner_name',
    header: formatMessage({ id: 'admin.orders.partner' }),
    ...fluidColumn({ min: 140, flex: 1.1 }),
    cell: ({ row }) => (
      // The whole row navigates to the partner detail; this button is the
      // visible per-cell affordance for the same destination. stopPropagation
      // keeps the row handler from firing a second, identical navigation.
      <button
        className="cursor-pointer truncate border-0 bg-transparent p-0 text-left font-medium text-text-primary hover:text-primary-main"
        title={row.original.partner_name}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          void navigate({
            to: '/admin/partners/$partnerId',
            params: { partnerId: row.original.partner_id },
          })
        }}
      >
        <Typography
          color="inherit"
          component="span"
          variant="body2"
        >
          {row.original.partner_name}
        </Typography>
      </button>
    ),
  }
}

export const AdminOrdersPage = () => {
  const { formatMessage } = useIntl()
  // Route-scoped navigate: types the `search` reducer against OrdersSearch
  // (the global useNavigate would union every route's search schema).
  const navigate = ordersRoute.useNavigate()
  const shortcuts = useDateShortcuts()
  const partnerColumn = usePartnerColumn()

  // ─── URL state is the single source of truth ──────────────────────────────
  const search = ordersRoute.useSearch()

  const updateSearch = (delta: Partial<OrdersSearch>, opts?: { resetPage?: boolean }) => {
    void navigate({
      to: '/admin/orders',
      search: (prev: OrdersSearch) => {
        const next = { ...prev, ...delta }
        if (opts?.resetPage !== false) {
          // Narrowing a filter must reset to page 1 — the current page index
          // could point past the end of the new result.
          next.page = undefined
        }

        return next
      },
      replace: true,
    })
  }

  // The search input renders a local draft and pushes it into the URL — and
  // with it the Orval query key, i.e. a BE round-trip — only after the typist
  // pauses. A per-keystroke write would navigate + refetch on every character.
  const [qDraft, setQDraft] = useState(search.q ?? '')
  const { debounced: writeQDraft } = useDebouncedCallback((value: string) => {
    updateSearch({ q: value === '' ? undefined : value })
  }, 300)

  // Keep the draft following external URL changes (back/forward, drill-down
  // links). Writes never originate here, so this cannot loop with the
  // debounced write above.
  useEffect(
    function syncDraftWithSearchQuery() {
      setQDraft(search.q ?? '')
    },
    [search.q],
  )

  // ─── Orders query (URL → BE params) ───────────────────────────────────────
  const listParams = toListOrdersParams(search, DEFAULT_PAGE_SIZE)
  const ordersQuery = useListOrders(listParams, {
    // Keeps the prior page rendered while the next page loads, so paging and
    // filter changes don't blank the table between fetches.
    query: { placeholderData: keepPreviousData },
  })
  const { data, isLoading, isError, refetch } = ordersQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global
  // sink. Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(ordersQuery, 'listOrders failed: ')

  // Resolve the partner-scope chip's label. Cached by the detail page's query
  // key, so arriving FROM a partner context renders the name instantly.
  const partnerQuery = useGetPartner(search.partner ?? '', {
    query: { enabled: search.partner !== undefined },
  })

  const rows = data?.orders ?? []
  const rowCount = data?.total ?? 0

  const paginationModel: PaginationState = {
    pageIndex: (search.page ?? 1) - 1, // URL is 1-based, the table is 0-based
    pageSize: DEFAULT_PAGE_SIZE,
  }

  // ─── Date range — URL stores YYYY-MM-DD; UI consumes Date pairs ───────────
  const dateRange: [Date | null, Date | null] = [
    search.from === undefined ? null : fromDateStr(search.from),
    search.to === undefined ? null : fromDateStr(search.to),
  ]

  const subtitle =
    data == null ? undefined : (
      <FormattedMessage
        id="admin.orders.subtitle"
        values={{ total: <FormattedNumber value={rowCount} /> }}
      />
    )

  const hasActiveChips = search.status !== undefined || search.partner !== undefined

  return (
    <Page>
      <Page.Header
        subtitle={subtitle}
        title={<FormattedMessage id="admin.orders.title" />}
      />

      <Page.Content>
        {/* Single toolbar row: status facet left, search + date range right —
            same grammar as /admin/partners. */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <SelectField
              className="min-w-[160px]"
              label={formatMessage({ id: 'admin.orders.filter.status' })}
              value={search.status ?? ALL}
              options={[
                { value: ALL, label: <FormattedMessage id="admin.orders.status.all" /> },
                ...COMMISSION_STATUSES.map((value) => ({
                  value,
                  label: <FormattedMessage id={STATUS_LABEL_IDS[value]} />,
                })),
              ]}
              onValueChange={(v) => {
                updateSearch({ status: COMMISSION_STATUSES.find((s) => s === v) })
              }}
            />

            <div className="ml-auto flex flex-wrap items-center gap-3">
              <SearchInput
                placeholder={formatMessage({ id: 'admin.orders.search' })}
                value={qDraft}
                onChange={(e) => {
                  setQDraft(e.target.value)
                  writeQDraft(e.target.value)
                }}
              />
              <DateRangePicker
                closeOnSelect={false}
                presets={shortcuts}
                value={dateRange}
                onChange={([newFrom, newTo], context) => {
                  if (context.validationError.some(Boolean)) {
                    return
                  }
                  updateSearch({
                    from: newFrom === null ? undefined : toDateStr(startOfDay(newFrom)),
                    to: newTo === null ? undefined : toDateStr(endOfDay(newTo)),
                  })
                }}
              />
            </div>
          </div>

          {hasActiveChips && (
            <div
              className="flex flex-wrap items-center gap-3"
              data-tst="orders-active-facets"
            >
              {search.status !== undefined && (
                <FacetChip
                  labelId="admin.orders.filter.status"
                  value={formatMessage({ id: STATUS_LABEL_IDS[search.status] })}
                  onRemove={() => updateSearch({ status: undefined })}
                />
              )}
              {search.partner !== undefined && (
                <FacetChip
                  labelId="admin.orders.partner"
                  value={partnerQuery.data?.display_name ?? search.partner.slice(0, 8)}
                  onRemove={() => updateSearch({ partner: undefined })}
                />
              )}
            </div>
          )}
        </div>

        {isError ? (
          <EmptyState
            description={<FormattedMessage id="admin.orders.error.description" />}
            title={<FormattedMessage id="admin.orders.error.title" />}
            action={
              <Button
                color="primary"
                data-tst="orders-retry"
                variant="outlined"
                onClick={() => {
                  void refetch()
                }}
              >
                <FormattedMessage id="admin.orders.error.retry" />
              </Button>
            }
          />
        ) : !isLoading && rowCount === 0 ? (
          <EmptyState title={<FormattedMessage id="admin.orders.empty" />} />
        ) : (
          <StatsCard
            icon={<ShoppingCartIcon />}
            title={<FormattedMessage id="admin.orders.tableTitle" />}
          >
            <CommissionsTable<AffiliateAdminOrderRowSchema>
              manualPagination
              showCustomerEmail
              data={rows}
              getRowId={(row) => row.id}
              leadingColumns={[partnerColumn]}
              loading={isLoading}
              pagination={paginationModel}
              rowCount={rowCount}
              onPaginationChange={(next) => {
                updateSearch({ page: next.pageIndex + 1 }, { resetPage: false })
              }}
              onRowClick={(row) => {
                void navigate({
                  to: '/admin/partners/$partnerId',
                  params: { partnerId: row.partner_id },
                })
              }}
            />
          </StatsCard>
        )}
      </Page.Content>
    </Page>
  )
}
